import React, { useState, useEffect, useCallback } from 'react';
import { fetchMenu, completePayment } from './api';
import { CartProvider } from './CartContext';
import { SessionProvider } from './SessionContext';
import Menu from './components/Menu';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import OrderTracking from './components/OrderTracking';
import Footer from './components/Footer';
import './App.css';

// Worldpay redirects to these paths after payment — they are NOT venue IDs
const PAYMENT_RESULT_PATHS = ['success', 'failure', 'error', 'cancel', 'pending', 'expiry'];

function TableNumberModal({ onSubmit, onClose, currentTable }) {
  const [value, setValue] = useState(currentTable ? String(currentTable) : '');

  const handleSubmit = (e) => {
    e.preventDefault();
    const num = parseInt(value, 10);
    if (num > 0) onSubmit(num);
  };

  return (
    <div className="table-modal-overlay" onClick={onClose ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}>
      <div className="table-modal">
        <h2>{currentTable ? 'Change Table' : 'Enter Table Number'}</h2>
        <p>{currentTable ? 'Update your table number' : 'Please enter your table number to continue'}</p>
        <form onSubmit={handleSubmit}>
          <input
            type="number"
            min="1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Table number"
            autoFocus
            className="table-modal-input"
          />
          <button type="submit" disabled={!value || parseInt(value, 10) < 1} className="table-modal-btn">
            {currentTable ? 'Update' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

function App() {
  const [venueId, setVenueId] = useState(null);
  const [tableNumber, setTableNumber] = useState(null);
  const [checkoutId, setCheckoutId] = useState(null);
  const [venue, setVenue] = useState(null);
  const [products, setProducts] = useState([]);
  const [menuConfig, setMenuConfig] = useState(null);
  const [redirectOrder, setRedirectOrder] = useState(null); // Order from Worldpay redirect fallback
  const [kitchen, setKitchen] = useState(null);
  const [waitTime, setWaitTime] = useState(null);
  const [view, setView] = useState('menu');
  const [currentOrder, setCurrentOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTableModal, setShowTableModal] = useState(false);
  const [needsTable, setNeedsTable] = useState(false);

  useEffect(() => {
    // URL format: /PublicId?table=1&checkout=1
    const pathParts = window.location.pathname.replace(/^\/+|\/+$/g, '').split('/');
    const v = pathParts[0] || null;
    const params = new URLSearchParams(window.location.search);

    // Handle Worldpay payment result redirects (e.g. /success, /failure)
    if (v && PAYMENT_RESULT_PATHS.includes(v.toLowerCase())) {
      const savedPayment = JSON.parse(localStorage.getItem('worldpay_pending_payment') || 'null');
      if (v.toLowerCase() === 'success' && savedPayment) {
        // Complete the payment using the saved reference
        setLoading(true);
        setVenueId(savedPayment.venue_id || null);
        completePayment({
          payment_reference: savedPayment.payment_reference,
          worldpay_order_key: params.get('orderKey') || null,
        })
          .then(orderResult => {
            localStorage.removeItem('worldpay_pending_payment');
            if (orderResult.duplicate && !orderResult.order_id) {
              // Already completed by the WPCL callback — redirect back to venue
              if (savedPayment.return_url) {
                window.location.href = savedPayment.return_url;
              } else {
                setError('Your order has already been placed. Check with staff if you need help.');
                setLoading(false);
              }
            } else {
              setRedirectOrder(orderResult);
              setLoading(false);
            }
          })
          .catch(err => {
            console.error('[PAYMENT-REDIRECT] Failed to complete payment:', err);
            setError('Payment received but we had trouble creating your order. Please contact staff — your payment is safe.');
            setLoading(false);
          });
      } else if (['failure', 'error'].includes(v.toLowerCase())) {
        const errorReason = params.get('errors') || 'Payment failed';
        const returnUrl = savedPayment?.return_url;
        setError(`Payment failed: ${decodeURIComponent(errorReason)}.`);
        if (returnUrl) {
          // Store return URL so the error page can offer a "try again" link
          setVenueId(savedPayment?.venue_id || null);
        }
        localStorage.removeItem('worldpay_pending_payment');
        setLoading(false);
      } else if (v.toLowerCase() === 'cancel') {
        const returnUrl = savedPayment?.return_url;
        setError('Payment was cancelled.');
        if (returnUrl) setVenueId(savedPayment?.venue_id || null);
        localStorage.removeItem('worldpay_pending_payment');
        setLoading(false);
      } else {
        localStorage.removeItem('worldpay_pending_payment');
        setError('Payment session expired or is pending. Please contact staff if you were charged.');
        setLoading(false);
      }
      return;
    }

    const t = params.get('table');
    const c = params.get('checkout');

    if (!v) {
      setError('No venue specified. Please scan a QR code to order.');
      setLoading(false);
      return;
    }

    setVenueId(v);
    setTableNumber(t ? parseInt(t, 10) : null);
    setCheckoutId(c ? parseInt(c, 10) : null);

    // If no table number in URL, show the table entry screen after menu loads
    if (!t) {
      setNeedsTable(true);
    }

    fetchMenu(v, c ? parseInt(c, 10) : null)
      .then(data => {
        setVenue(data.venue);
        setProducts(data.products);
        setMenuConfig(data.menu_config);
        setKitchen(data.kitchen);
        setWaitTime(data.wait_time);
        document.documentElement.style.setProperty('--primary', data.venue.primary_color);
        document.documentElement.style.setProperty('--accent', data.venue.accent_color);
        document.title = `Order - ${data.venue.name}`;
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleTableSubmit = useCallback((num) => {
    setTableNumber(num);
    setNeedsTable(false);
    setShowTableModal(false);
  }, []);

  if (loading) return <div className="loading">Loading menu...</div>;
  if (error) {
    const savedPayment = JSON.parse(localStorage.getItem('worldpay_pending_payment') || 'null');
    const returnUrl = savedPayment?.return_url;
    return (
      <div className="error-page">
        <h2>Oops</h2>
        <p>{error}</p>
        {returnUrl && (
          <a href={returnUrl} className="error-page-retry-link" style={{ display: 'inline-block', marginTop: '1rem', padding: '0.75rem 1.5rem', background: 'var(--primary, #6366f1)', color: '#fff', borderRadius: '0.5rem', textDecoration: 'none' }}>
            Try Again
          </a>
        )}
      </div>
    );
  }

  // Show order tracking after successful Worldpay redirect payment
  if (redirectOrder) {
    return (
      <SessionProvider venueId={venueId} tableNumber={redirectOrder.table_number}>
        <div className="app">
          <OrderTracking
            order={redirectOrder}
            venueId={venueId}
            onNewOrder={() => {
              // Navigate back to the venue's ordering page
              const savedPayment = JSON.parse(localStorage.getItem('worldpay_pending_payment') || 'null');
              const returnUrl = savedPayment?.return_url;
              if (returnUrl) {
                window.location.href = returnUrl;
              } else if (venueId) {
                window.location.href = `/${venueId}`;
              } else {
                window.location.href = '/';
              }
            }}
          />
          <Footer />
        </div>
      </SessionProvider>
    );
  }

  // Check if mobile ordering is available (at least one payment method enabled)
  const paymentMethods = venue?.payment_methods || [];
  const orderingAvailable = paymentMethods.includes('worldpay') || paymentMethods.includes('pay_at_counter') || paymentMethods.includes('cash');

  if (!orderingAvailable) {
    return (
      <div className="app">
        <header className="app-header">
          {venue.logo_url && <img src={venue.logo_url} alt={venue.name} className="venue-logo" />}
          {!venue.hide_venue_name && <h1>{venue.name}</h1>}
        </header>
        <div className="error-page">
          <h2>Unavailable</h2>
          <p>Mobile ordering isn't currently available at this venue.</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Show table entry screen if no table number (but not if kitchen is closed)
  if (needsTable) {
    return (
      <div className="app">
        <header className="app-header">
          {venue.logo_url && <img src={venue.logo_url} alt={venue.name} className="venue-logo" />}
          {!venue.hide_venue_name && <h1>{venue.name}</h1>}
        </header>
        {kitchen && !kitchen.is_open ? (
          <div className="kitchen-closed-card">
            <div className="kitchen-closed-card__icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <h2 className="kitchen-closed-card__title">Kitchen is closed</h2>
            {kitchen.opens_at && (
              <p className="kitchen-closed-card__time">
                {kitchen.next_open_day ? `Opens ${kitchen.next_open_day} at ${kitchen.opens_at}` : `Opens at ${kitchen.opens_at}`}
              </p>
            )}
            <p className="kitchen-closed-card__sub">Check back later to place your order</p>
          </div>
        ) : (
          <TableNumberModal onSubmit={handleTableSubmit} />
        )}
        <Footer />
      </div>
    );
  }

  return (
    <SessionProvider venueId={venueId} tableNumber={tableNumber} checkoutId={checkoutId}>
      <CartProvider>
        <div className="app">
          <header className="app-header">
            {venue.logo_url && <img src={venue.logo_url} alt={venue.name} className="venue-logo" />}
            {!venue.hide_venue_name && <h1>{venue.name}</h1>}
            {tableNumber && (
              <span
                className="table-badge table-badge--tappable"
                onClick={() => setShowTableModal(true)}
                role="button"
                tabIndex={0}
                title="Tap to change table"
              >
                Table {tableNumber}
              </span>
            )}
          </header>

          {/* Table change modal */}
          {showTableModal && (
            <TableNumberModal
              currentTable={tableNumber}
              onSubmit={handleTableSubmit}
              onClose={() => setShowTableModal(false)}
            />
          )}

          {!kitchen.is_open && (
            <div className="kitchen-closed-banner">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>
                Kitchen is closed{kitchen.opens_at ? ` — ${kitchen.next_open_day ? `opens ${kitchen.next_open_day} at ${kitchen.opens_at}` : `opens at ${kitchen.opens_at}`}` : ''}
              </span>
            </div>
          )}

          {view === 'menu' && <Menu products={products} menuConfig={menuConfig} disabled={!kitchen.is_open} waitTime={waitTime} onViewCart={() => setView('cart')} />}
          {view === 'cart' && <Cart products={products} onBack={() => setView('menu')} onCheckout={() => setView('checkout')} />}
          {view === 'checkout' && <Checkout venue={venue} venueId={venueId} tableNumber={tableNumber} checkoutId={checkoutId} onBack={() => setView('cart')} onOrderPlaced={(order) => { setCurrentOrder(order); setView('tracking'); }} />}
          {view === 'tracking' && <OrderTracking order={currentOrder} venueId={venueId} onNewOrder={() => { setCurrentOrder(null); setView('menu'); }} />}

          <Footer />
        </div>
      </CartProvider>
    </SessionProvider>
  );
}

export default App;
